import { Box, CircularProgress, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useCallback, useEffect, useState } from 'react'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import AlertUI from '../../generic/AlertUI'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import MosInfoProvider from '../Widgets/common/provider/MosInfoProvider'
import WidgetGrid from '../Widgets/base/WidgetGrid'
import { parseTemplateWidgets } from '../widgetTemplates/widgetTemplateParse'
import { serializeTemplateWidgets } from '../widgetTemplates/widgetTemplateUtils'

const ENDPOINT_BASE = 'widgets/initialScreenTemplates'
const HEADER_HEIGHT = '3.5rem'

/**
 * Full-screen "edit and apply" for a user initial-screen template: loads the
 * template layout into an editable grid so the admin can tweak it and apply the
 * RESULT to the target user's screen (POST user-screen/{userId}) in one go. The
 * template itself is never modified and no new template is created — the edited
 * layout is one-off. Applying overwrites the user's screen, so it always asks
 * for confirmation, like every other apply in the catalog.
 * onClose(applied) — applied === true when the layout was applied to the user.
 */
const EditAndApplyUserScreenDialog = ({ open, template, user, onClose }) => {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [widgets, setWidgets] = useState([])
  const [editing, setEditing] = useState(true)
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    if (!open || !template?.id) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const full = await apiGet(ENDPOINT_BASE + '/' + template.id)
        if (!cancelled) setWidgets(parseTemplateWidgets(full?.widgets))
      } catch (e) {
        if (!cancelled) showAlert({ title: t_('error loading widget template'), message: t_(e.message || 'Unknown error'), severity: 'error' })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [open, template, reloadToken, showAlert, t_])

  const reloadWidgets = useCallback(() => setReloadToken((token) => token + 1), [])

  const doApply = useCallback(async () => {
    if (!user) return
    setBusy(true)
    try {
      await apiPost(ENDPOINT_BASE + '/user-screen/' + user.id, serializeTemplateWidgets(widgets))
      setConfirmOpen(false)
      onClose(true)
    } catch (e) {
      setConfirmOpen(false)
      showAlert({ title: '', message: t_(e.message || 'Unknown error'), severity: 'error' })
    } finally {
      setBusy(false)
    }
  }, [user, widgets, onClose, showAlert, t_])

  return (
    <Dialog fullScreen open={open} onClose={() => onClose(false)} PaperProps={{ sx: { overflow: 'hidden' } }}>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ px: 2, height: HEADER_HEIGHT, flexShrink: 0, boxSizing: 'border-box', borderBottom: 1, borderColor: 'divider' }}>
        <Tooltip title={t_('Back')}>
          <IconButton onClick={() => onClose(false)} aria-label={t_('Back')}><ArrowBackIcon /></IconButton>
        </Tooltip>
        <Typography variant='h6' sx={{ whiteSpace: 'nowrap' }}>{t_('Edit and apply')}</Typography>
        <Typography color='text.secondary' noWrap sx={{ flexGrow: 1 }}>
          {(template?.name || '') + (user ? ' — ' + user.username : '')}
        </Typography>
      </Stack>
      <Box sx={{ height: 'calc(100vh - ' + HEADER_HEIGHT + ')', overflow: 'auto' }}>
        <Stack direction='column' sx={{ height: '100%' }}>
          <Box sx={{ flexGrow: 1 }}>
            <div style={{ position: 'sticky', top: '0.5rem', zIndex: 100, padding: '0 1rem' }}>{AlertElement}</div>
            {loading
              ? <Stack justifyContent='center' alignItems='center' sx={{ height: '100%' }}><CircularProgress size='6rem' /></Stack>
              : (
                <MosInfoProvider>
                  <WidgetGrid
                    widgets={widgets}
                    setWidgets={setWidgets}
                    reloadWidgets={reloadWidgets}
                    onSave={() => setConfirmOpen(true)}
                    editing={editing}
                    setEditing={setEditing}
                    showAllWidgets={false}
                    toolbarLabel={template?.name || ''}
                  />
                </MosInfoProvider>
                )}
          </Box>
        </Stack>
      </Box>

      <SmartDialog
        setOpen={confirmOpen}
        title={createDialogTitle({ color: 'warning', title: t_('Attention'), icon: WarningAmberIcon })}
        message={t_('Applying this template will overwrite the initial screen of this user. Continue?')}
        accept
        acceptLabel={t_('Continue')}
        acceptCallback={doApply}
        disableButtons={busy}
        cancel
        cancelCallback={() => setConfirmOpen(false)}
      />
    </Dialog>
  )
}

export default EditAndApplyUserScreenDialog
